import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { Trans } from '@ui/i18n/trans';
import { useInvoiceData } from './requests/use-invoice-data';
import { Skeleton } from '@ui/skeleton/skeleton';
import { FormattedDate } from '@ui/i18n/formatted-date';
import { FormattedCurrency } from '@ui/i18n/formatted-currency';
import { Button } from '@ui/buttons/button';
import { DownloadIcon } from '@ui/icons/material/Download';
import { useSettings } from '@ui/settings/use-settings';
import Logo from '../../syscom.png';
import Paid from '../../paid.jpg';
import { PDFDownloadLink } from '@react-pdf/renderer';
import InvoicePreview from './invoice-preview';

interface InvoiceViewDialogProps {
  invoiceId: number;
}


export function InvoiceViewDialog({invoiceId}: InvoiceViewDialogProps) {
  const query = useInvoiceData(invoiceId);
  const {base_url} = useSettings();

  const invoiceData = query.data?.data;

  return (
    <Dialog size="xl" className="max-h-[90vh] overflow-hidden">
      <DialogHeader className="px-16 md:px-24 py-16">
        <Trans message="Invoice" />
      </DialogHeader>
      <DialogBody className="px-0 md:px-0 pb-16 max-h-[calc(90vh-120px)] overflow-y-auto">
        {query.isLoading ? (
          <InvoiceSkeleton />
        ) : invoiceData ? (
          <div className="max-w-4xl mx-auto" style={{ backgroundColor: 'var(--be-surface)', color: 'var(--be-text-main)' }}>
            {/* Invoice Header with Logo - Mobile Optimized */}
            <div style={{ background: 'linear-gradient(to right, var(--be-primary), rgba(var(--be-primary-rgb), 0.8))', color: 'var(--be-on-primary)' }} className="p-16 md:p-24 rounded-t-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-16">
                <div className="flex items-center space-x-12 md:space-x-16">
                  {/* Logo */}
                  <img
                    src={Logo}
                    alt="Syscom Logo"
                    className="w-96 h-96 md:w-128 md:h-128 rounded-lg object-contain flex-shrink-0"
                    style={{ backgroundColor: 'rgba(var(--be-on-primary-rgb), 0.2)' }}
                  />
                </div>
                <div className="text-left sm:text-right flex flex-col items-start sm:items-end space-y-8 md:space-y-0 md:space-x-16 md:flex-col md:items-end">
                  {/* Paid Status Image */}
                  <div className="w-32 h-24 md:w-36 md:h-24 rounded-full flex items-center justify-center order-2 sm:order-1" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                    <img src={Paid} alt="Paid" className="w-28 h-20 md:w-32 md:h-24" />
                  </div>
                  <div className="order-1 sm:order-2">
                    <p className="text-sm md:text-base font-medium" style={{ color: 'var(--be-on-primary)' }}>Invoice No. {invoiceData.number}</p>
                    <p className="text-xs md:text-sm" style={{ color: 'rgba(var(--be-on-primary-rgb), 0.8)' }}>Created: {(invoiceData as any).created_at || 'N/A'}</p>
                    <p className="text-xs md:text-sm" style={{ color: 'rgba(var(--be-on-primary-rgb), 0.8)' }}>Due: {(invoiceData as any).due_at || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Company and Customer Info - Mobile Optimized */}
            <div className="p-16 md:p-24" style={{ backgroundColor: 'var(--be-background-chip)' }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
                {/* From (Company) */}
                <div>
                  <h3 className="font-bold mb-8 text-sm md:text-base" style={{ color: 'var(--be-text-main)' }}>
                    <Trans message="From" />
                  </h3>
                  <div className="text-xs md:text-sm space-y-1" style={{ color: 'var(--be-text-secondary)' }}>
                    <p className="font-medium">Syscom Entertainment & Leisure Limited</p>
                    <p>Suite 312, Atlas House</p>
                    <p>Caxton Close, Wigan</p>
                    <p>WN3 6XU, United Kingdom</p>
                  </div>
                </div>

                {/* Bill To (Customer) */}
                {invoiceData.customer && (
                  <div className="text-left md:text-right">
                    <h3 className="font-bold mb-8 text-sm md:text-base" style={{ color: 'var(--be-text-main)' }}>
                      <Trans message="Bill To" />
                    </h3>
                    <div className="text-xs md:text-sm space-y-1" style={{ color: 'var(--be-text-secondary)' }}>
                      <p className="font-medium">{invoiceData.customer.name}</p>
                      <p>{invoiceData.customer.email}</p>
                      {invoiceData.customer.address && (
                        <p>{invoiceData.customer.address}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Items - Mobile Optimized */}
            <div className="p-16 md:p-24">
              {/* Desktop Table View */}
              <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--be-surface)', borderColor: 'var(--be-divider)' }}>
                <table className="w-full">
                  <thead className="bg-gray-50" style={{ backgroundColor: 'var(--be-background-chip)' }}>
                    <tr>
                      <th className="px-16 py-12 text-left font-bold text-gray-800" style={{ color: 'var(--be-text-main)' }}>
                        <Trans message="LICENSE" />
                      </th>
                      <th className="px-16 py-12 text-center font-bold text-gray-800" style={{ color: 'var(--be-text-main)' }}>
                        <Trans message="COST" />
                      </th>
                      <th className="px-16 py-12 text-center font-bold text-gray-800" style={{ color: 'var(--be-text-main)' }}>
                        <Trans message="QUANTITY" />
                      </th>
                      <th className="px-16 py-12 text-right font-bold text-gray-800" style={{ color: 'var(--be-text-main)' }}>
                        <Trans message="AMOUNT" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" style={{ borderColor: 'var(--be-divider)' }}>
                    {(invoiceData as any).packages?.map((item: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50" style={{ backgroundColor: 'var(--be-surface)' }}>
                        <td className="px-16 py-12 text-gray-700" style={{ color: 'var(--be-text-main)' }}>{item.name}</td>
                         <td className="px-16 py-12 text-center" style={{ color: 'var(--be-text-main)' }}>
                          £{item.price}
                        </td>
                        <td className="px-16 py-12 text-center" style={{ color: 'var(--be-text-main)' }}>
                          {item.qty}
                        </td>
                        <td className="px-16 py-12 text-right font-medium text-gray-800" style={{ color: 'var(--be-text-main)' }}>
                          £{item.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200" style={{ backgroundColor: 'var(--be-background-chip)', borderColor: 'var(--be-divider)' }}>
                    <tr>
                      <td colSpan={3} className="px-16 py-8 text-right font-medium" style={{ color: 'var(--be-text-main)' }}>
                        Order Amount:
                      </td>
                      <td className="px-16 py-8 text-right font-medium" style={{ color: 'var(--be-text-main)' }}>
                        £{(invoiceData as any).amount || (invoiceData as any).total || '0.00'}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-16 py-8 text-right font-medium" style={{ color: 'var(--be-text-main)' }}>
                        Discount:
                      </td>
                      <td className="px-16 py-8 text-right font-medium" style={{ color: 'var(--be-text-main)' }}>
                        £{(invoiceData as any).discount || '0.00'}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3} className="px-16 py-8 text-right font-bold" style={{ color: 'var(--be-text-main)' }}>
                        Order Value:
                      </td>
                      <td className="px-16 py-8 text-right font-bold" style={{ color: 'var(--be-primary)' }}>
                        £{(invoiceData as any).total || '0.00'}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="px-16 py-4 text-center text-sm" style={{ color: 'var(--be-text-secondary)' }}>
                        (Taxes/charges applied separately)
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-12">
                {(invoiceData as any).packages?.map((item: any, index: number) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-16" style={{ backgroundColor: 'var(--be-surface)', borderColor: 'var(--be-divider)' }}>
                    <div className="space-y-8">
                      <div className="flex justify-between items-start">
                        <h4 className="font-medium text-sm" style={{ color: 'var(--be-text-main)' }}>{item.name}</h4>
                        <span className="font-bold text-sm" style={{ color: 'var(--be-primary)' }}>£{item.total}</span>
                      </div>
                      <div className="flex justify-between text-xs" style={{ color: 'var(--be-text-secondary)' }}>
                        <span>Cost: £{item.price}</span>
                        <span>Qty: {item.qty}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Mobile Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-16 space-y-8" style={{ backgroundColor: 'var(--be-background-chip)', borderColor: 'var(--be-divider)' }}>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--be-text-main)' }}>Order Amount:</span>
                    <span style={{ color: 'var(--be-text-main)' }}>£{(invoiceData as any).amount || (invoiceData as any).total || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--be-text-main)' }}>Discount:</span>
                    <span style={{ color: 'var(--be-text-main)' }}>£{(invoiceData as any).discount || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold border-t border-gray-200 pt-8" style={{ borderColor: 'var(--be-divider)' }}>
                    <span style={{ color: 'var(--be-text-main)' }}>Order Value:</span>
                    <span style={{ color: 'var(--be-primary)' }}>£{(invoiceData as any).total || '0.00'}</span>
                  </div>
                  <div className="text-center text-xs" style={{ color: 'var(--be-text-secondary)' }}>
                    (Taxes/charges applied separately)
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Status - Mobile Optimized */}
            <div className="p-16 md:p-24 bg-gray-50 rounded-b-lg" style={{ backgroundColor: 'var(--be-background-chip)' }}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-16">
                <div className="flex items-center space-x-8 md:space-x-12">
                  {invoiceData.paid ? (
                    <div className="flex items-center space-x-8">
                      {/* Paid Status Image - Replace this div with your actual paid image */}
                      <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-bold text-sm">✓</span>
                      </div>
                      <div>
                        {/* <p className="font-bold text-green-600">PAID</p> */}
                        {/* <p className="text-sm text-gray-600">Payment received</p> */}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-8">
                      {/* Unpaid Status Image - Replace this div with your actual unpaid/pending image */}
                      {/* <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 font-bold text-sm">!</span>
                      </div>
                      <div>
                        <p className="font-bold text-red-600">UNPAID</p>
                        <p className="text-sm text-gray-600">Payment pending</p>
                      </div> */}
                    </div>
                  )}
                </div>

                <div className="flex gap-8 md:gap-12 w-full sm:w-auto">
                  <PDFDownloadLink
                    document={<InvoicePreview invoiceData={invoiceData} />}
                    className="text-white w-full sm:w-auto"
                    style={{ textDecoration: 'none' }}
                    fileName={`invoice_${invoiceData?.number}.pdf`}
                  >
                    {({ blob, url, loading, error }) => (
                      <Button
                        variant="outline"
                        size="sm"
                        startIcon={<DownloadIcon />}
                        disabled={loading}
                        className="w-full sm:w-auto"
                      >
                        {loading ? 'Loading document...' : <Trans message="Download PDF" />}
                      </Button>
                    )}
                  </PDFDownloadLink>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted py-32">
            <Trans message="Invoice data not available" />
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}

function InvoiceSkeleton() {
  return (
    <div className="max-w-4xl mx-auto" style={{ backgroundColor: 'var(--be-surface)' }}>
      {/* Header Skeleton - Mobile Optimized */}
      <div style={{ background: 'linear-gradient(to right, var(--be-primary), rgba(var(--be-primary-rgb), 0.8))' }} className="p-16 md:p-24 rounded-t-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-16">
          <Skeleton className="h-96 w-96 md:h-128 md:w-128 rounded-lg" />
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-8 md:gap-16">
            <Skeleton className="h-24 w-32 md:h-24 md:w-36 rounded-full" />
            <div className="space-y-4">
              <Skeleton className="h-16 w-32" />
              <Skeleton className="h-12 w-24" />
              <Skeleton className="h-12 w-20" />
            </div>
          </div>
        </div>
      </div>

      {/* Company/Customer Info Skeleton - Mobile Optimized */}
      <div className="p-16 md:p-24" style={{ backgroundColor: 'var(--be-background-chip)' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
          <div className="space-y-8">
            <Skeleton className="h-16 w-20" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2" />
          </div>
          <div className="space-y-8">
            <Skeleton className="h-16 w-20" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </div>
      </div>

      {/* Items Table/Card Skeleton - Mobile Optimized */}
      <div className="p-16 md:p-24">
        {/* Desktop Table Skeleton */}
        <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--be-surface)', borderColor: 'var(--be-divider)' }}>
          <Skeleton className="h-48 w-full" />
          <div className="p-16 space-y-12">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-3/4" />
          </div>
        </div>

        {/* Mobile Card Skeleton */}
        <div className="md:hidden space-y-12">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </div>

      {/* Payment Status Skeleton - Mobile Optimized */}
      <div className="p-16 md:p-24 rounded-b-lg" style={{ backgroundColor: 'var(--be-background-chip)' }}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-16">
          <div className="flex items-center space-x-8">
            <Skeleton className="h-32 w-32 rounded-full" />
            <div className="space-y-4">
              <Skeleton className="h-16 w-20" />
              <Skeleton className="h-12 w-32" />
            </div>
          </div>
          <Skeleton className="h-32 w-24" />
        </div>
      </div>
    </div>
  );
}
